import React from 'react';
import SugarIconSelector from '../../SugarIconSelector';
import { Button } from '../../../../ui/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { cn } from '../../../../ui/utils/cn';

interface FormatOptionsProps {
    coffeeType: string;
    coffeeTypesCache: Record<string, any>;
    format: any;
    setFormat: (newFormat: any) => void;
    formatOptionsSelected: boolean;
    setFormatOptionsSelected: (v: boolean) => void;
    handleSugarChange: (level: string, type: string) => void;
    handleNestedFormatChange: (category: string, subcategory: string, value: string) => void;
    handleFormatChange: (key: string, value: string) => void;
}

const FormatOptions: React.FC<FormatOptionsProps> = ({
    coffeeType,
    coffeeTypesCache,
    format,
    setFormat,
    formatOptionsSelected,
    setFormatOptionsSelected,
    handleSugarChange,
    handleNestedFormatChange,
    handleFormatChange,
}) => {
    if (!coffeeType || !coffeeTypesCache[coffeeType]) return null;
    const formatConfig = coffeeTypesCache[coffeeType].coffeeFormat || {};

    return (
        <div className="space-y-3">
            <label className="text-xs text-gray-500">format options</label>
            {Object.keys(formatConfig).length > 0 ? (
                !formatOptionsSelected ? (
                    <div className="space-y-4">
                        {Object.entries(formatConfig).map(([category, options]) => {
                            const pretty = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                            if (category === 'sugar') {
                                const sugarOptions = options as { level?: string[]; type?: string[] };
                                if (sugarOptions.level && sugarOptions.level.length > 0) {
                                    return (
                                        <div key={category} className="space-y-2">
                                            <label className="text-xs text-gray-500">sugar</label>
                                            <SugarIconSelector value={format?.sugar} onChange={handleSugarChange} showTitle={false} />
                                        </div>
                                    );
                                }
                                return null;
                            }
                            if (category === 'milk') {
                                const milkOptions = options as { level?: string[]; type?: string[] };
                                if (milkOptions.type && milkOptions.type.length > 0) {
                                    return (
                                        <div key={category} className="space-y-2">
                                            <label className="text-xs text-gray-500">milk type</label>
                                            <div className="flex flex-wrap gap-2">
                                                {milkOptions.type.map((type) => {
                                                    const isSelected = format?.milk?.type === type;
                                                    const displayName = type.replace(/-/g, ' ');
                                                    return (
                                                        <button
                                                            key={type}
                                                            type="button"
                                                            onClick={() => handleNestedFormatChange('milk', 'type', type)}
                                                            className={cn(
                                                                "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                                                                "border",
                                                                isSelected
                                                                    ? "bg-mint/20 border-mint text-mint-dark"
                                                                    : "bg-white border-gray-200 text-gray-600 hover:border-mint/50 hover:bg-mint/5"
                                                            )}
                                                        >
                                                            {displayName}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }
                            if (category === 'ice-cream') {
                                const iceCreamOptions = options as { level?: string[]; type?: string[] };
                                if (iceCreamOptions.type && iceCreamOptions.type.length > 0) {
                                    return (
                                        <div key={category} className="space-y-2">
                                            <label className="text-sm font-medium">{pretty('ice cream flavor')}</label>
                                            <select
                                                className={cn(
                                                    "flex h-10 w-full rounded-none border-brutal border-ink bg-background px-3 py-2 text-sm",
                                                    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                    "disabled:cursor-not-allowed disabled:opacity-50 shadow-inset"
                                                )}
                                                value={format?.['ice-cream']?.type || ''}
                                                onChange={(e) => handleNestedFormatChange('ice-cream', 'type', e.target.value)}
                                            >
                                                <option value="">Select ice cream flavor</option>
                                                {iceCreamOptions.type.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type.replace(/-/g, ' ').charAt(0).toUpperCase() + type.replace(/-/g, ' ').slice(1)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                }
                                return null;
                            }
                            if (category === 'type' && Array.isArray(options) && (options as string[]).length > 0) {
                                return (
                                    <div key={category} className="space-y-2">
                                        <label className="text-sm font-medium">{pretty('brew method')}</label>
                                        <select
                                            className={cn(
                                                "flex h-10 w-full rounded-none border-brutal border-ink bg-background px-3 py-2 text-sm",
                                                "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                "disabled:cursor-not-allowed disabled:opacity-50 shadow-inset",
                                                "appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iNiIgdmlld0JveD0iMCAwIDEwIDYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw1IDVMOSAxIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4=')] bg-no-repeat bg-[right_0.75rem_center] pr-8"
                                            )}
                                            value={format?.type || ''}
                                            onChange={(e) => handleFormatChange('type', e.target.value)}
                                        >
                                            <option value="">Select brew method</option>
                                            {(options as string[]).map((type) => (
                                                <option key={type} value={type}>
                                                    {type.replace(/-/g, ' ').charAt(0).toUpperCase() + type.replace(/-/g, ' ').slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            }
                            // generic renderer for any extra categories like "syrup"
                            // supports either array form (e.g., syrup: [..]) or object form (e.g., syrup: { type: [..], ... })
                            if (Array.isArray(options) && (options as string[]).length > 0) {
                                return (
                                    <div key={category} className="space-y-2">
                                        <label className="text-sm font-medium">{pretty(category)}</label>
                                        <select
                                            className={cn(
                                                "flex h-10 w-full rounded-none border-brutal border-ink bg-background px-3 py-2 text-sm",
                                                "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                "disabled:cursor-not-allowed disabled:opacity-50 shadow-inset",
                                                "appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iNiIgdmlld0JveD0iMCAwIDEwIDYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw1IDVMOSAxIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4=')] bg-no-repeat bg-[right_0.75rem_center] pr-8"
                                            )}
                                            value={format?.[category] || ''}
                                            onChange={(e) => handleFormatChange(category, e.target.value)}
                                        >
                                            <option value="">Select {pretty(category)}</option>
                                            {(options as string[]).map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt.replace(/-/g, ' ').charAt(0).toUpperCase() + opt.replace(/-/g, ' ').slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            }

                            if (typeof options === 'object' && options !== null) {
                                const entries = Object.entries(options as Record<string, any>).filter(([, v]) => Array.isArray(v) && v.length > 0);
                                if (entries.length > 0) {
                                    return (
                                        <div key={category} className="space-y-4">
                                            <label className="text-sm font-medium">{pretty(category)}</label>
                                            {entries.map(([sub, values]) => (
                                                <div key={`${category}-${sub}`} className="space-y-2">
                                                    <label className="text-xs text-muted-foreground">{pretty(sub)}</label>
                                                    <select
                                                        className={cn(
                                                            "flex h-10 w-full rounded-none border-brutal border-ink bg-background px-3 py-2 text-sm",
                                                            "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                            "disabled:cursor-not-allowed disabled:opacity-50 shadow-inset"
                                                        )}
                                                        value={format?.[category]?.[sub] || ''}
                                                        onChange={(e) => handleNestedFormatChange(category, sub, e.target.value)}
                                                    >
                                                        <option value="">Select {pretty(sub)}</option>
                                                        {(values as string[]).map((opt) => (
                                                            <option key={opt} value={opt}>
                                                                {opt.replace(/-/g, ' ').charAt(0).toUpperCase() + opt.replace(/-/g, ' ').slice(1)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                }
                            }

                            return null;
                        })}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            {Object.entries(format || {}).map(([key, value]) => {
                                const sugar = key === 'sugar' && value && typeof value === 'object' ? (value as any) : null;
                                if (sugar) {
                                    return (
                                        <div key={key} className="flex items-center justify-between gap-3 p-3 border-brutal border-ink bg-background shadow-brutal-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium">Sugar:</span>
                                                <span className="text-sm">
                                                    {sugar.level ? String(sugar.level).replace(/-/g, ' ') : 'None'}
                                                    {sugar.type && sugar.level !== 'no-sugar' ? ` (${String(sugar.type)})` : ''}
                                                </span>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    const newFormat = { ...(format || {}) };
                                                    delete newFormat[key];
                                                    setFormat(newFormat);
                                                    setFormatOptionsSelected(false);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </div>
                                    );
                                }
                                const milk = key === 'milk' && value && typeof value === 'object' ? (value as any) : null;
                                if (milk) {
                                    return (
                                        <div key={key} className="flex items-center justify-between gap-3 p-3 border-brutal border-ink bg-background shadow-brutal-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium">Milk:</span>
                                                <span className="text-sm">{milk.type ? String(milk.type).replace(/-/g, ' ') : 'None'}</span>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    const newFormat = { ...(format || {}) };
                                                    delete newFormat[key];
                                                    setFormat(newFormat);
                                                    setFormatOptionsSelected(false);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </div>
                                    );
                                }
                                if (typeof value === 'string') {
                                    return (
                                        <div key={key} className="flex items-center justify-between gap-3 p-3 border-brutal border-ink bg-background shadow-brutal-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium">{key.replace(/-/g, ' ')}:</span>
                                                <span className="text-sm">{value.replace(/-/g, ' ')}</span>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    const newFormat = { ...(format || {}) };
                                                    delete newFormat[key];
                                                    setFormat(newFormat);
                                                    setFormatOptionsSelected(false);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                        <Button
                            type="button"
                            variant="sky"
                            onClick={() => setFormatOptionsSelected(false)}
                        >
                            Modify
                        </Button>
                    </div>
                )
            ) : (
                <div className="text-sm text-muted-foreground italic">
                    <p>No format options available for this coffee type.</p>
                </div>
            )}
        </div>
    );
};

export default FormatOptions;


